// TRAINING (owner 2026-11-02) render layer: training dummies (crystal-totem targets), the in-world ARMORY (instanced
// pedestals + one icon atlas drawn on instanced camera-facing quads) and the range / hitbox view. Render-only: it reads
// sim.training / sim state and never writes them. Everything is created once in the GameView constructor (hidden, count 0)
// so the prewarm's compile pass builds its pipelines before any run; nothing is drawn outside training runs.
import * as THREE from 'three/webgpu';
import { float, instancedBufferAttribute, texture, uv, vec2 } from 'three/tsl';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { Sim } from '../core/sim';
import { MAX_WEAPON_LEVEL, PASSIVES, WEAPONS, type PassiveId, type WeaponId } from '../core/content';
import { BEAM_LEN } from '../core/skillStats';
import { skillStats } from '../core/skillStats';
import type { FusionId } from '../core/fusions';
import { ARMORY_R, type Pedestal } from '../core/training';
import { iconSource } from '../app/icons';
import { toonMaterial, type RenderStyle } from './toon';

const DUMMY_CAP = 64, PED_CAP = 128, RING_SEG = 48, RANGE_CAP = 48;
/** atlas: 1024² canvas, 10 x 10 cells of 102 px */
const ATLAS = 1024, CELLS = 10, CELL = ATLAS / CELLS;
const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _q = new THREE.Quaternion(), _s = new THREE.Vector3(), _c = new THREE.Color(), _e = new THREE.Euler();

/** a crystal-totem training target: stone base, wooden post, ring target, a crystal on top (vertex-coloured, ~2.1 m) */
function dummyGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const col = (g: THREE.BufferGeometry, hex: number) => { const c = new THREE.Color(hex), n = g.attributes.position.count, a = new Float32Array(n * 3); for (let i = 0; i < n; i++) { a[i * 3] = c.r; a[i * 3 + 1] = c.g; a[i * 3 + 2] = c.b; } g.setAttribute('color', new THREE.BufferAttribute(a, 3)); const ng = g.index ? g.toNonIndexed() : g; ng.deleteAttribute('uv'); parts.push(ng); };
  col(new THREE.CylinderGeometry(0.5, 0.6, 0.25, 8).translate(0, 0.12, 0), 0x8c8478);
  col(new THREE.CylinderGeometry(0.1, 0.12, 1.55, 6).translate(0, 1.0, 0), 0x8a6440);
  col(new THREE.BoxGeometry(0.9, 0.1, 0.1).translate(0, 1.15, 0), 0x7a5634);
  for (const [r, c, z] of [[0.55, 0xd8443a, 0.06], [0.4, 0xf4ecd8, 0.08], [0.24, 0xd8443a, 0.1], [0.1, 0xf4ecd8, 0.12]] as const)
    col(new THREE.CylinderGeometry(r, r, 0.04, 20).rotateX(Math.PI / 2 - 0.55).translate(0, 1.42, z), c); // tilted back: reads from the high camera
  col(new THREE.OctahedronGeometry(0.22, 0).scale(0.8, 1.5, 0.8).translate(0, 2.05, 0), 0x8ff4ff);
  return mergeGeometries(parts)!;
}

export class TrainingView {
  readonly group = new THREE.Group();
  private readonly dummies: THREE.InstancedMesh;
  private readonly peds: THREE.InstancedMesh;
  private readonly rings: THREE.InstancedMesh;
  private readonly icons: THREE.InstancedMesh;
  private readonly iconUv: THREE.InstancedBufferAttribute;
  private readonly range: THREE.LineSegments;
  private readonly rangePos: Float32Array; private readonly rangeCol: Float32Array;
  private readonly canvas: HTMLCanvasElement; private readonly atlasTex: THREE.CanvasTexture;
  private atlasFor: readonly Pedestal[] | null = null;
  private t = 0;
  /** range / hitbox view on (WORLD tab) */
  showRange = false;
  /** last swap flash seen (hero burst) */
  private lastSwap = 0;

  constructor(scene: THREE.Scene, style: RenderStyle, private readonly groundAt: (x: number, z: number) => number, private readonly burst: (x: number, z: number) => void) {
    const cel = style === 'cel';
    const dm = cel ? toonMaterial({ color: 0xffffff }) : new THREE.MeshStandardNodeMaterial({ color: 0xffffff, roughness: 0.8, flatShading: true });
    dm.vertexColors = true;
    this.dummies = new THREE.InstancedMesh(dummyGeometry(), dm, DUMMY_CAP);
    this.dummies.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(DUMMY_CAP * 3).fill(1), 3);
    this.dummies.castShadow = true;
    // pedestals: a squat hex plinth with a lighter cap
    const pg = mergeGeometries([new THREE.CylinderGeometry(0.85, 0.95, 0.5, 6).translate(0, 0.25, 0), new THREE.CylinderGeometry(0.7, 0.8, 0.12, 6).translate(0, 0.56, 0)])!;
    const pm = cel ? toonMaterial({ color: 0xffffff }) : new THREE.MeshStandardNodeMaterial({ color: 0xffffff, roughness: 0.85, flatShading: true });
    this.peds = new THREE.InstancedMesh(pg, pm, PED_CAP);
    this.peds.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(PED_CAP * 3).fill(1), 3);
    this.peds.receiveShadow = true;
    // glow ring on the ground (state colour; bright + pulsing for the pedestal the hero stands at)
    const rm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.rings = new THREE.InstancedMesh(new THREE.RingGeometry(ARMORY_R - 0.18, ARMORY_R, 40).rotateX(-Math.PI / 2), rm, PED_CAP);
    this.rings.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(PED_CAP * 3), 3);
    this.rings.renderOrder = 2;
    // icons: one atlas, per-instance cell offset (one draw call for every pedestal)
    this.canvas = document.createElement('canvas'); this.canvas.width = this.canvas.height = ATLAS;
    this.atlasTex = new THREE.CanvasTexture(this.canvas); this.atlasTex.colorSpace = THREE.SRGBColorSpace; this.atlasTex.anisotropy = 4;
    this.iconUv = new THREE.InstancedBufferAttribute(new Float32Array(PED_CAP * 2), 2);
    const im = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const off = instancedBufferAttribute(this.iconUv) as any;
    const tx = texture(this.atlasTex, uv().mul(vec2(1 / CELLS, 1 / CELLS)).add(off));
    im.colorNode = tx.rgb; im.opacityNode = tx.a.mul(float(1));
    this.icons = new THREE.InstancedMesh(new THREE.PlaneGeometry(1.25, 1.25), im, PED_CAP);
    this.icons.renderOrder = 4;
    // range view: one dynamic line set (vertex colours)
    this.rangePos = new Float32Array(RANGE_CAP * RING_SEG * 6); this.rangeCol = new Float32Array(RANGE_CAP * RING_SEG * 6);
    const lg = new THREE.BufferGeometry();
    lg.setAttribute('position', new THREE.BufferAttribute(this.rangePos, 3).setUsage(THREE.DynamicDrawUsage));
    lg.setAttribute('color', new THREE.BufferAttribute(this.rangeCol, 3).setUsage(THREE.DynamicDrawUsage));
    this.range = new THREE.LineSegments(lg, new THREE.LineBasicNodeMaterial({ vertexColors: true, transparent: true, opacity: 0.9, depthWrite: false }));
    this.range.renderOrder = 3;
    for (const o of [this.dummies, this.peds, this.rings, this.icons, this.range]) { o.frustumCulled = false; this.group.add(o); }
    this.dummies.count = 0; this.peds.count = 0; this.rings.count = 0; this.icons.count = 0; lg.setDrawRange(0, 0);
    scene.add(this.group);
  }

  /** draw the armory icons into the atlas once per pedestal layout (painted art, else the SVG glyph; name under it) */
  private buildAtlas(peds: readonly Pedestal[]): void {
    this.atlasFor = peds;
    const ctx = this.canvas.getContext('2d')!; ctx.clearRect(0, 0, ATLAS, ATLAS);
    peds.forEach((p, i) => {
      if (i >= CELLS * CELLS) return;
      const cx = (i % CELLS) * CELL, cy = Math.floor(i / CELLS) * CELL;
      const name = p.kind === 'fusion' ? p.id : p.kind === 'trait' ? PASSIVES[p.id as PassiveId].name : WEAPONS[p.id as WeaponId].name;
      const back = p.kind === 'fusion' ? '#0d3b2a' : p.kind === 'trait' ? '#2a1f45' : '#14243f', rim = p.kind === 'fusion' ? '#5dffb4' : p.kind === 'trait' ? '#c9a8ff' : '#8fd8ff';
      ctx.save();
      ctx.beginPath(); ctx.arc(cx + CELL / 2, cy + CELL * 0.42, CELL * 0.4, 0, Math.PI * 2); ctx.fillStyle = back; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = rim; ctx.stroke();
      ctx.font = `bold ${Math.round(CELL * 0.13)}px sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
      ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(0,0,0,.85)'; ctx.fillStyle = '#ffffff';
      const label = (p.kind === 'fusion' ? name.toUpperCase() : name).slice(0, 16);
      ctx.strokeText(label, cx + CELL / 2, cy + CELL - 3); ctx.fillText(label, cx + CELL / 2, cy + CELL - 3);
      ctx.restore();
      const src = iconSource(p.kind, p.id), size = CELL * 0.62, ix = cx + (CELL - size) / 2, iy = cy + CELL * 0.42 - size / 2;
      const draw = (img: HTMLImageElement) => { if (this.atlasFor !== peds) return; ctx.save(); ctx.beginPath(); ctx.arc(cx + CELL / 2, cy + CELL * 0.42, CELL * 0.37, 0, Math.PI * 2); ctx.clip(); ctx.drawImage(img, ix, iy, size, size); ctx.restore(); this.atlasTex.needsUpdate = true; };
      const svg = () => { const img = new Image(); img.onload = () => draw(img); img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(src.svg.replace('<svg ', `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" `)); };
      if (src.url) { const img = new Image(); img.onload = () => draw(img); img.onerror = svg; img.src = src.url; } else svg();
    });
    // the per-instance atlas cell (v grows downward in canvas space; texture v is flipped)
    peds.forEach((_, i) => { this.iconUv.setXY(i, (i % CELLS) / CELLS, 1 - (Math.floor(i / CELLS) + 1) / CELLS); });
    this.iconUv.needsUpdate = true; this.atlasTex.needsUpdate = true;
  }

  sync(sim: Sim, camera: THREE.Camera, dt: number): void {
    const tr = sim.training;
    this.group.visible = !!tr;
    if (!tr) return;
    this.t += dt;
    // ---- dummies (face the hero; hit flash; type tint) ----
    let n = 0; const p = sim.player;
    for (const e of sim.enemies) {
      if (!e.alive || !e.dummy || n >= DUMMY_CAP) continue;
      const k = (e.def.radius / 0.75) * (e.elite ? 1.25 : 1), yaw = Math.atan2(camera.position.x - e.x, camera.position.z - e.z); // the target faces the camera
      const wob = e.flash > 0 ? Math.sin(this.t * 60) * 0.06 * e.flash * 10 : 0;
      _e.set(wob, yaw, 0, 'YXZ'); _q.setFromEuler(_e);
      _p.set(e.x, this.groundAt(e.x, e.z), e.z); _s.set(k, k, k);
      this.dummies.setMatrixAt(n, _m.compose(_p, _q, _s));
      const base = e.dummy === 2 ? 0xffd86a : e.dummy === 3 ? 0x9fb8d8 : e.dummy === 4 ? 0xd8a8ff : 0xffffff;
      _c.setHex(base); if (e.flash > 0) _c.lerp(new THREE.Color(0xffffff), 0.6).multiplyScalar(1.6);
      if (e.formT > 0) _c.multiplyScalar(1.5 + Math.sin(this.t * 20) * 0.5);
      this.dummies.setColorAt(n, _c); n++;
    }
    this.dummies.count = n; this.dummies.instanceMatrix.needsUpdate = true; if (this.dummies.instanceColor) this.dummies.instanceColor.needsUpdate = true;
    // ---- armory ----
    const peds = tr.pedestals;
    if (peds !== this.atlasFor) this.buildAtlas(peds);
    const cq = camera.quaternion;
    for (let i = 0; i < peds.length && i < PED_CAP; i++) {
      const pd = peds[i], gy = this.groundAt(pd.x, pd.z), near = tr.near === i;
      let owned = false, top = false;
      if (pd.kind === 'skill') { const l = sim.wlv(pd.id as WeaponId); owned = l > 0; top = l >= MAX_WEAPON_LEVEL || sim.evolved.has(pd.id as WeaponId); }
      else if (pd.kind === 'trait') { const l = sim.plv(pd.id as PassiveId); owned = l > 0; top = l >= (PASSIVES[pd.id as PassiveId].max ?? 5); }
      else { owned = sim.fusion.has(pd.id as FusionId); top = owned; }
      _q.identity(); _p.set(pd.x, gy, pd.z); _s.set(1, 1, 1);
      this.peds.setMatrixAt(i, _m.compose(_p, _q, _s));
      _c.setHex(pd.kind === 'fusion' ? (owned ? 0x5dffb4 : 0x4f8f78) : top ? 0xffd45e : owned ? (pd.kind === 'trait' ? 0xc9a8ff : 0x8fd8ff) : 0x8a8276);
      this.peds.setColorAt(i, _c);
      _p.set(pd.x, gy + 0.04, pd.z); this.rings.setMatrixAt(i, _m.compose(_p, _q, _s));
      const glow = near ? 2.2 + Math.sin(this.t * 6) * 0.6 : owned ? 1.1 : 0.35;
      _c.setHex(pd.kind === 'fusion' ? 0x5dffb4 : top ? 0xffd45e : pd.kind === 'trait' ? 0xc9a8ff : 0x7fd8ff).multiplyScalar(glow);
      this.rings.setColorAt(i, _c);
      const bob = Math.sin(this.t * 1.6 + i * 0.7) * 0.12, sc = near ? 1.25 : 1;
      _p.set(pd.x, gy + 1.75 + bob, pd.z); _s.set(sc, sc, sc);
      this.icons.setMatrixAt(i, _m.compose(_p, cq, _s));
    }
    const np = Math.min(PED_CAP, peds.length);
    this.peds.count = np; this.rings.count = np; this.icons.count = np;
    for (const o of [this.peds, this.rings, this.icons]) { o.instanceMatrix.needsUpdate = true; if (o.instanceColor) o.instanceColor.needsUpdate = true; }
    // ---- swap flash on the hero ----
    if (tr.swapT > this.lastSwap + 0.05) this.burst(p.x, p.z);
    this.lastSwap = tr.swapT;
    // ---- range / hitbox view ----
    this.syncRange(sim);
  }

  private syncRange(sim: Sim): void {
    const g = this.range.geometry;
    if (!this.showRange) { g.setDrawRange(0, 0); return; }
    const p = sim.player, gy = this.groundAt(p.x, p.z) + 0.08;
    let v = 0; const P = this.rangePos, C = this.rangeCol;
    const seg = (x0: number, z0: number, x1: number, z1: number, c: THREE.Color) => {
      if (v + 2 > P.length / 3) return;
      P[v * 3] = x0; P[v * 3 + 1] = gy; P[v * 3 + 2] = z0; C[v * 3] = c.r; C[v * 3 + 1] = c.g; C[v * 3 + 2] = c.b; v++;
      P[v * 3] = x1; P[v * 3 + 1] = gy; P[v * 3 + 2] = z1; C[v * 3] = c.r; C[v * 3 + 1] = c.g; C[v * 3 + 2] = c.b; v++;
    };
    const ring = (r: number, c: THREE.Color, dash = false) => {
      for (let k = 0; k < RING_SEG; k++) { if (dash && k % 2) continue; const a0 = (k / RING_SEG) * Math.PI * 2, a1 = ((k + 1) / RING_SEG) * Math.PI * 2; seg(p.x + Math.cos(a0) * r, p.z + Math.sin(a0) * r, p.x + Math.cos(a1) * r, p.z + Math.sin(a1) * r, c); }
    };
    let hue = 0;
    for (const [id, lv] of sim.weapons) {
      if (id === 'surge') continue;
      const c = new THREE.Color().setHSL((hue += 0.13) % 1, 0.9, 0.62).multiplyScalar(2.2); // over 1: the bloom makes the thin lines read
      if (id === 'lance') { const w = sim.beamWidth, ax = p.aimX, az = p.aimZ, px = -az, pz = ax; seg(p.x + px * w, p.z + pz * w, p.x + px * w + ax * BEAM_LEN, p.z + pz * w + az * BEAM_LEN, c); seg(p.x - px * w, p.z - pz * w, p.x - px * w + ax * BEAM_LEN, p.z - pz * w + az * BEAM_LEN, c); continue; }
      const st = skillStats(sim, id, lv, sim.evolved.has(id));
      let k = 0;
      for (const r of st.rows) if (r.f === 'm' && typeof r.v === 'number' && r.v > 0.3 && k < 3) { ring(r.v, c, k > 0); k++; }
    }
    (g.attributes.position as THREE.BufferAttribute).needsUpdate = true; (g.attributes.color as THREE.BufferAttribute).needsUpdate = true;
    g.setDrawRange(0, v);
  }
}
